import {
	ApiOutlined,
	AreaChartOutlined,
	BankOutlined,
	CheckCircleOutlined,
	CloudServerOutlined,
	CreditCardOutlined,
	EnvironmentOutlined,
	EyeOutlined,
	FileSearchOutlined,
	FileTextOutlined,
	HistoryOutlined,
	HomeOutlined,
	KeyOutlined,
	LeftOutlined,
	LineChartOutlined,
	MedicineBoxOutlined,
	RightOutlined,
	SafetyCertificateOutlined,
	ScanOutlined,
	SettingOutlined,
	SyncOutlined,
	TeamOutlined,
} from "@ant-design/icons";
import { Button, Menu, Space } from "antd";
import type { FC } from "react";
import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./dfp-scroll-navigation.scss";

interface ScrollableNavigationBarProps {
	tabNames: string[];
	basePath: string;
}

interface TabDimensions {
	widths: number[];
	totalWidth: number;
}

interface ScrollState {
	canScrollLeft: boolean;
	canScrollRight: boolean;
	tabDimensions: TabDimensions;
	selectedTabIndex: number;
}

type ScrollAction =
	| { type: "SET_TAB_DIMENSIONS"; payload: TabDimensions }
	| {
			type: "SET_SCROLL_STATE";
			payload: { canScrollLeft: boolean; canScrollRight: boolean };
	  }
	| { type: "SET_SELECTED_TAB"; payload: number };

const iconMap: Record<string, JSX.Element> = {
	Overview: <HomeOutlined />,
	Virtual: <CloudServerOutlined />,
	Assets: <SafetyCertificateOutlined />,
	"Predictive Spend": <LineChartOutlined />,
	Submissions: <FileTextOutlined />,
	Maintenance: <MedicineBoxOutlined />,
	Settings: <SettingOutlined />,
	Analytics: <AreaChartOutlined />,
	"Asset Map": <EnvironmentOutlined />,
	Users: <TeamOutlined />,
	"Vision Viewer": <EyeOutlined />,
	"Data Update": <ScanOutlined />,
	"Monthly Audit": <HistoryOutlined />,
	"Master Asset Types": <KeyOutlined />,
	"Org Asset Types": <BankOutlined />,
	Reports: <FileSearchOutlined />,
	"OCR Template Tool": <ScanOutlined />,
	"Vision Trainer": <EyeOutlined />,
	"Sync Settings": <SyncOutlined />,
	Subscription: <CreditCardOutlined />,
	"Quality Check": <CheckCircleOutlined />,
	Integrations: <ApiOutlined />,
};

const initialState: ScrollState = {
	canScrollLeft: false,
	canScrollRight: true,
	tabDimensions: {
		widths: [],
		totalWidth: 0,
	},
	selectedTabIndex: 0,
};

const scrollReducer = (
	state: ScrollState,
	action: ScrollAction,
): ScrollState => {
	switch (action.type) {
		case "SET_TAB_DIMENSIONS":
			return {
				...state,
				tabDimensions: action.payload,
			};
		case "SET_SCROLL_STATE":
			return {
				...state,
				canScrollLeft: action.payload.canScrollLeft,
				canScrollRight: action.payload.canScrollRight,
			};
		case "SET_SELECTED_TAB":
			return {
				...state,
				selectedTabIndex: action.payload,
			};
		default:
			return state;
	}
};

const calculateTabWidth = (index: number, widths: number[]): number => {
	return widths.slice(0, index).reduce((sum, width) => sum + width, 0);
};

const ScrollableNavigationBar: FC<ScrollableNavigationBarProps> = ({
	tabNames,
	basePath,
}) => {
	const [state, dispatch] = useReducer(scrollReducer, initialState);
	const navRef = useRef<HTMLDivElement>(null);
	const location = useLocation();
	const navigate = useNavigate();
	const [isMobile, setIsMobile] = useState(window.innerWidth <= 640);
	const [isNavigating, setIsNavigating] = useState(false);
	const navigationTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
	const hasNormalizedUrl = useRef(false);

	// Cleanup function to clear any pending navigation timeouts
	useEffect(() => {
		return () => {
			if (navigationTimeoutRef.current) {
				clearTimeout(navigationTimeoutRef.current);
			}
		};
	}, []);

	// Get the current path segment and handle empty or root paths
	const currentPath = useMemo(() => {
		// Return empty string if no tabs are available
		if (!tabNames || tabNames.length === 0) {
			return "";
		}

		const pathSegments = location.pathname.split("/").filter(Boolean);
		const baseSegments = basePath.split("/").filter(Boolean);

		// Find the index of the last base segment
		const baseIndex = pathSegments.findIndex(
			(segment) => segment === baseSegments[baseSegments.length - 1],
		);

		// If we can't find the base path or we're at the root, use the first tab
		if (baseIndex === -1 || baseIndex === pathSegments.length - 1) {
			return tabNames[0].toLowerCase().replace(/\s+/g, "-");
		}

		// Get the next segment after the base path and remove any additional segments
		const nextSegment = pathSegments[baseIndex + 1];
		return nextSegment.split("/")[0];
	}, [location.pathname, basePath, tabNames]);

	useEffect(() => {
		const handleResize = () => {
			setIsMobile(window.innerWidth <= 640);
		};

		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	// Update selected tab index whenever the route changes
	useEffect(() => {
		const selectedIndex = tabNames.findIndex(
			(tab) => tab.toLowerCase().replace(/\s+/g, "-") === currentPath,
		);

		if (selectedIndex >= 0) {
			dispatch({ type: "SET_SELECTED_TAB", payload: selectedIndex });

			// Scroll the selected tab into view for desktop view
			if (!isMobile && navRef.current) {
				const tabElements = document.querySelectorAll(".nav-tab");
				const selectedTab = tabElements[selectedIndex] as HTMLElement;
				if (selectedTab) {
					const containerWidth = navRef.current.clientWidth;
					const tabLeft = selectedTab.offsetLeft;
					const tabWidth = selectedTab.offsetWidth;

					// Calculate the scroll position to center the tab
					const scrollPosition = tabLeft - containerWidth / 2 + tabWidth / 2;
					navRef.current.scrollTo({ left: scrollPosition, behavior: "smooth" });
				}
			}
		}
	}, [currentPath, tabNames, isMobile]);

	useEffect(() => {
		const updateTabDimensions = (): void => {
			const tabElements = document.querySelectorAll(".nav-tab");
			const widths = Array.from(tabElements).map(
				(tab) => tab.getBoundingClientRect().width,
			);
			const totalWidth = widths.reduce((sum, width) => sum + width, 0);

			dispatch({
				type: "SET_TAB_DIMENSIONS",
				payload: { widths, totalWidth },
			});

			checkScrollPosition();
		};

		updateTabDimensions();
		window.addEventListener("resize", updateTabDimensions);
		return () => window.removeEventListener("resize", updateTabDimensions);
	}, []);

	const checkScrollPosition = (): void => {
		if (!navRef.current) return;

		const { scrollLeft, scrollWidth, clientWidth } = navRef.current;
		const maxScrollLeft = scrollWidth - clientWidth;

		dispatch({
			type: "SET_SCROLL_STATE",
			payload: {
				canScrollLeft: scrollLeft > 0,
				canScrollRight: scrollLeft < maxScrollLeft,
			},
		});
	};

	const handleScroll = (direction: "left" | "right"): void => {
		if (!navRef.current) return;

		const scrollAmount = direction === "left" ? -150 : 150;
		navRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
	};

	const getTabPath = (tab: string) => {
		const normalizedTab = tab.toLowerCase().replace(/\s+/g, "-");
		return `${basePath}/${normalizedTab}`;
	};

	const handleTabClick = (e: React.MouseEvent, tab: string) => {
		e.preventDefault();
		if (isNavigating) return;

		const targetPath = getTabPath(tab);
		if (location.pathname !== targetPath) {
			setIsNavigating(true);

			// Clear any existing timeout
			if (navigationTimeoutRef.current) {
				clearTimeout(navigationTimeoutRef.current);
			}

			// Set a new timeout for navigation
			navigationTimeoutRef.current = setTimeout(() => {
				// Replace the current history entry
				window.history.replaceState(null, "", targetPath);

				// Navigate using React Router
				navigate(targetPath, { replace: true });

				// Reset navigation state after a short delay
				setTimeout(() => {
					setIsNavigating(false);
				}, 100);
			}, 0);
		}
	};

	// Handle initial route on page load/refresh - only run once
	useEffect(() => {
		if (hasNormalizedUrl.current) return;

		const pathSegments = location.pathname.split("/").filter(Boolean);
		const baseSegments = basePath.split("/").filter(Boolean);
		const baseIndex = pathSegments.findIndex(
			(segment) => segment === baseSegments[baseSegments.length - 1],
		);

		if (baseIndex !== -1 && baseIndex < pathSegments.length - 1) {
			const currentSegment = pathSegments[baseIndex + 1];
			const normalizedSegment = currentSegment.split("/")[0];
			const targetPath = `${basePath}/${normalizedSegment}`;

			if (
				location.pathname !== targetPath &&
				!location.pathname.startsWith(targetPath)
			) {
				window.history.replaceState(null, "", targetPath);
				hasNormalizedUrl.current = true;
			}
		}

		hasNormalizedUrl.current = true;
	}, []); // Empty deps - only run once on mount

	// Don't render if no tabs are available
	if (!tabNames || tabNames.length === 0) {
		return null;
	}

	const menuItems = tabNames.map((tab) => ({
		key: tab.toLowerCase().replace(/\s+/g, "-"),
		label: (
			<button
				type="button"
				className={`nav-button ${tab.toLowerCase().replace(/\s+/g, "-") === currentPath ? "active" : ""}`}
				onClick={(e) => handleTabClick(e, tab)}
				disabled={isNavigating}
			>
				{iconMap[tab]}
				<span>{tab}</span>
			</button>
		),
	}));

	return (
		<div className="scroll-navigation-container mx-2">
			{isMobile ? (
				<Menu
					mode="horizontal"
					selectedKeys={[currentPath]}
					items={menuItems}
				/>
			) : (
				<Space className="navigation-wrapper d-flex">
					<Button
						type="text"
						icon={<LeftOutlined />}
						onClick={() => handleScroll("left")}
						disabled={!state.canScrollLeft}
						className="scroll-button"
					/>

					<div
						ref={navRef}
						className="tabs-container"
						onScroll={checkScrollPosition}
					>
						<div className="tabs-wrapper">
							{tabNames.map((tab, index) => (
								<button
									key={tab}
									type="button"
									className={`nav-tab ${tab.toLowerCase().replace(/\s+/g, "-") === currentPath ? "active" : ""}`}
									onClick={(e) => handleTabClick(e, tab)}
									disabled={isNavigating}
								>
									{iconMap[tab]}
									<span>{tab}</span>
								</button>
							))}
							<div
								className="tab-indicator"
								style={{
									left: `${calculateTabWidth(state.selectedTabIndex, state.tabDimensions.widths)}px`,
									width: `${state.tabDimensions.widths[state.selectedTabIndex] || 0}px`,
								}}
							/>
						</div>
					</div>

					<Button
						type="text"
						icon={<RightOutlined />}
						onClick={() => handleScroll("right")}
						disabled={!state.canScrollRight}
						className="scroll-button"
					/>
				</Space>
			)}
		</div>
	);
};

export { ScrollableNavigationBar };
